// Factual airport data sourced from official airport websites, FAA, and TSA
// Data accurate as of 2024-2025

export interface AirportDetails {
  code: string;
  terminals: number;
  terminalNames: string[];
  annualPassengers: string; // in millions
  hubAirlines: string[];
  peakHours: {
    morning: string;
    evening: string;
    busiestDays: string[];
  };
  parking: {
    economy: string;
    garage: string;
    valet?: string;
  };
  transitOptions: string[];
  tipsSecurity: string[];
  tipsGeneral: string[];
  avgDomesticWait: string;
  avgInternationalWait: string;
  preCheckLanes: boolean;
  clearLanes: boolean;
}

export const airportDetails: Record<string, AirportDetails> = {
  ATL: {
    code: "ATL",
    terminals: 2,
    terminalNames: ["Domestic Terminal", "International Terminal (Concourse F)"],
    annualPassengers: "93.7",
    hubAirlines: ["Delta Air Lines", "Southwest Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$14/day",
      garage: "$36/day",
      valet: "$45/day"
    },
    transitOptions: ["MARTA Rail (direct to downtown)", "Rental cars", "Rideshare (Uber/Lyft)", "Hotel shuttles"],
    tipsSecurity: [
      "Use the South Security checkpoint for shorter waits during peak hours",
      "TSA PreCheck lanes available at all checkpoints",
      "CLEAR lanes available for expedited screening"
    ],
    tipsGeneral: [
      "The Plane Train connects all concourses underground",
      "Concourse E has the most dining options",
      "Free WiFi available throughout the airport"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  LAX: {
    code: "LAX",
    terminals: 9,
    terminalNames: ["Terminal 1", "Terminal 2", "Terminal 3", "Terminal 4", "Terminal 5", "Terminal 6", "Terminal 7", "Terminal 8", "Tom Bradley International Terminal (TBIT)"],
    annualPassengers: "88.1",
    hubAirlines: ["American Airlines", "Delta Air Lines", "United Airlines", "Southwest Airlines"],
    peakHours: {
      morning: "6:00 AM - 9:00 AM",
      evening: "3:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$12/day (Lot C)",
      garage: "$40/day",
      valet: "$55/day"
    },
    transitOptions: ["LAX-it Shuttle to Metro", "FlyAway Bus", "Rental cars", "Rideshare pickup at LAX-it"],
    tipsSecurity: [
      "Terminal 4 (American) often has the longest waits",
      "TBIT has dedicated TSA PreCheck and CLEAR lanes",
      "Early morning international flights create congestion at TBIT"
    ],
    tipsGeneral: [
      "Use the Automated People Mover (APM) to connect terminals",
      "Allow extra time - LAX traffic is notoriously heavy",
      "Terminals 4-8 are connected airside post-security"
    ],
    avgDomesticWait: "20-35 minutes",
    avgInternationalWait: "30-50 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  ORD: {
    code: "ORD",
    terminals: 4,
    terminalNames: ["Terminal 1 (United)", "Terminal 2 (Delta, Air Canada)", "Terminal 3 (American, Spirit)", "Terminal 5 (International)"],
    annualPassengers: "83.2",
    hubAirlines: ["United Airlines", "American Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:30 AM",
      evening: "3:30 PM - 6:30 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$15/day (Lot F)",
      garage: "$39/day",
      valet: "$52/day"
    },
    transitOptions: ["CTA Blue Line (direct to downtown)", "Pace buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal 3 (American) typically has the longest waits",
      "Terminal 5 requires a separate security screening",
      "Use the underground tunnel to walk between Terminal 1, 2, and 3"
    ],
    tipsGeneral: [
      "ATS train connects all terminals airside",
      "Terminals 1-3 are connected post-security",
      "Yoga room available in Terminal 3"
    ],
    avgDomesticWait: "20-30 minutes",
    avgInternationalWait: "25-45 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  DFW: {
    code: "DFW",
    terminals: 5,
    terminalNames: ["Terminal A", "Terminal B", "Terminal C", "Terminal D", "Terminal E"],
    annualPassengers: "73.4",
    hubAirlines: ["American Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$10/day (Express South)",
      garage: "$27/day",
      valet: "$30/day"
    },
    transitOptions: ["DART Orange Line", "TEXRail to Fort Worth", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal D (international) has the most consistent wait times",
      "Terminal A and C handle most American Airlines flights",
      "Each terminal has its own security checkpoint"
    ],
    tipsGeneral: [
      "Skylink train connects all terminals airside in 10 minutes",
      "Free cell phone waiting lot available",
      "DFW is one of the largest airports by area in the US"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  DEN: {
    code: "DEN",
    terminals: 1,
    terminalNames: ["Jeppesen Terminal (Concourses A, B, C)"],
    annualPassengers: "69.3",
    hubAirlines: ["United Airlines", "Southwest Airlines", "Frontier Airlines"],
    peakHours: {
      morning: "5:00 AM - 7:30 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$13/day (Pikes Peak lot)",
      garage: "$30/day",
      valet: "$39/day"
    },
    transitOptions: ["RTD A Line to Union Station", "SkyRide buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Main terminal security feeds to all concourses",
      "Bridge security checkpoint on Level 6 often has shorter lines",
      "South security checkpoint is typically faster than North"
    ],
    tipsGeneral: [
      "Automated train connects terminal to all concourses",
      "Airport is 25 miles from downtown Denver",
      "Blucifer statue is visible on approach"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  JFK: {
    code: "JFK",
    terminals: 6,
    terminalNames: ["Terminal 1", "Terminal 2 (Delta)", "Terminal 4", "Terminal 5 (JetBlue)", "Terminal 7", "Terminal 8 (American)"],
    annualPassengers: "62.5",
    hubAirlines: ["JetBlue Airways", "Delta Air Lines", "American Airlines"],
    peakHours: {
      morning: "6:00 AM - 9:00 AM",
      evening: "4:00 PM - 8:00 PM",
      busiestDays: ["Thursday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$18/day (Federal Circle)",
      garage: "$39/day"
    },
    transitOptions: ["AirTrain to Jamaica/Howard Beach", "LIRR from Jamaica", "NYC Subway", "Express buses"],
    tipsSecurity: [
      "Terminal 4 (international) can have long waits for evening departures",
      "Terminal 5 (JetBlue) has efficient security processing",
      "Each terminal has separate security - no airside connections"
    ],
    tipsGeneral: [
      "AirTrain connects all terminals ($8.50 to subway)",
      "No landside connections between terminals - use AirTrain",
      "TWA Hotel in Terminal 5 for unique layover experience"
    ],
    avgDomesticWait: "20-35 minutes",
    avgInternationalWait: "35-60 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  SFO: {
    code: "SFO",
    terminals: 4,
    terminalNames: ["Terminal 1 (Southwest, JetBlue)", "Terminal 2 (American)", "Terminal 3 (United)", "International Terminal"],
    annualPassengers: "57.5",
    hubAirlines: ["United Airlines"],
    peakHours: {
      morning: "6:00 AM - 9:00 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$20/day (Long Term)",
      garage: "$36/day"
    },
    transitOptions: ["BART (direct to downtown SF)", "SamTrans buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal 3 (United) has the most traffic",
      "International Terminal security can be slow for evening Asia departures",
      "Terminal 2 often has the shortest waits"
    ],
    tipsGeneral: [
      "AirTrain connects all terminals",
      "All domestic terminals are connected post-security",
      "SFO Museum has rotating art exhibits throughout terminals"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  SEA: {
    code: "SEA",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses A, B, C, D, N, S)"],
    annualPassengers: "50.9",
    hubAirlines: ["Alaska Airlines", "Delta Air Lines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$32/day",
      garage: "$39/day"
    },
    transitOptions: ["Link Light Rail", "Sound Transit buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Checkpoint 1 serves Concourses A/B, Checkpoint 2 serves C/D",
      "Checkpoint 5 (south) often has shorter lines",
      "N Gates require additional security for international flights"
    ],
    tipsGeneral: [
      "Central Terminal connects to all concourses",
      "Light rail to downtown takes 40 minutes",
      "Free Wi-Fi throughout the airport"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  LAS: {
    code: "LAS",
    terminals: 2,
    terminalNames: ["Terminal 1 (Gates A, B, C, D)", "Terminal 3 (Gates D, E)"],
    annualPassengers: "57.7",
    hubAirlines: ["Southwest Airlines", "Spirit Airlines", "Allegiant Air"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "7:00 PM - 10:00 PM",
      busiestDays: ["Thursday", "Friday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$16/day",
      garage: "$20/day (Terminal 1), $22/day (Terminal 3)",
      valet: "$40/day"
    },
    transitOptions: ["RTC bus routes", "Rental cars", "Rideshare", "Taxis"],
    tipsSecurity: [
      "Sunday afternoons are extremely busy (weekend departures)",
      "Terminal 1 handles more volume than Terminal 3",
      "Consider arriving extra early for weekend travel"
    ],
    tipsGeneral: [
      "Slot machines available throughout terminals",
      "Terminal 3 is connected to Terminal 1 airside",
      "Convention traffic creates unpredictable peaks"
    ],
    avgDomesticWait: "20-30 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  MCO: {
    code: "MCO",
    terminals: 2,
    terminalNames: ["Main Terminal (Airsides 1, 2, 3, 4)", "Terminal C (opened 2022)"],
    annualPassengers: "50.6",
    hubAirlines: ["Southwest Airlines", "JetBlue Airways", "Spirit Airlines"],
    peakHours: {
      morning: "6:00 AM - 9:00 AM",
      evening: "2:00 PM - 5:00 PM",
      busiestDays: ["Saturday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$10/day",
      garage: "$19/day",
      valet: "$25/day"
    },
    transitOptions: ["SunRail (from Terminal C)", "Lynx buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal C has newer, faster security lanes",
      "Main Terminal East security is typically faster than West",
      "Theme park crowds create morning rushes"
    ],
    tipsGeneral: [
      "Automated People Mover connects to all airsides",
      "New Terminal C opened in 2022 with modern amenities",
      "Disney, Universal traffic creates predictable peaks"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  EWR: {
    code: "EWR",
    terminals: 3,
    terminalNames: ["Terminal A", "Terminal B (United)", "Terminal C (United)"],
    annualPassengers: "46.3",
    hubAirlines: ["United Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:30 AM",
      evening: "3:30 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$20/day",
      garage: "$39/day"
    },
    transitOptions: ["AirTrain to NJ Transit", "NJ Transit trains", "Express buses", "Rideshare"],
    tipsSecurity: [
      "Terminal A recently renovated with improved security",
      "Terminal C handles most United international flights",
      "Terminal B can have long waits during morning rush"
    ],
    tipsGeneral: [
      "AirTrain connects all terminals ($8.25 to train)",
      "New Terminal A opened 2023 with modern facilities",
      "Allow extra time for NYC traffic"
    ],
    avgDomesticWait: "20-30 minutes",
    avgInternationalWait: "30-45 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  CLT: {
    code: "CLT",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses A, B, C, D, E)"],
    annualPassengers: "53.8",
    hubAirlines: ["American Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$9/day",
      garage: "$22/day"
    },
    transitOptions: ["CATS Sprinter bus", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Checkpoint D is typically less crowded",
      "American Airlines hub creates consistent morning rush",
      "TSA PreCheck significantly reduces wait times here"
    ],
    tipsGeneral: [
      "Rocking chairs throughout terminal for relaxation",
      "All concourses connected post-security",
      "Excellent hub for American Airlines connections"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  PHX: {
    code: "PHX",
    terminals: 3,
    terminalNames: ["Terminal 3", "Terminal 4 (largest)"],
    annualPassengers: "46.3",
    hubAirlines: ["American Airlines", "Southwest Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$10/day",
      garage: "$28/day"
    },
    transitOptions: ["PHX Sky Train", "Valley Metro Light Rail", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal 4 has multiple checkpoints - check which is closest to your gate",
      "A Gates checkpoint often has shorter lines",
      "Terminal 3 handles Southwest exclusively"
    ],
    tipsGeneral: [
      "PHX Sky Train connects to light rail",
      "Terminal 4 is the main terminal for most airlines",
      "Free charging stations throughout terminals"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  IAH: {
    code: "IAH",
    terminals: 5,
    terminalNames: ["Terminal A", "Terminal B", "Terminal C (United)", "Terminal D", "Terminal E (International)"],
    annualPassengers: "45.3",
    hubAirlines: ["United Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:30 AM",
      evening: "3:30 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$10/day",
      garage: "$26/day",
      valet: "$34/day"
    },
    transitOptions: ["Metro Red Line", "SuperShuttle", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal E (international) can have extended waits",
      "Terminal C is busiest with United traffic",
      "Each terminal has its own security checkpoint"
    ],
    tipsGeneral: [
      "Skyway train connects all terminals",
      "United Club locations in Terminals B, C, and E",
      "Terminal D opened 2023 with updated facilities"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  MIA: {
    code: "MIA",
    terminals: 1,
    terminalNames: ["North Terminal (Concourses D, E, F)", "Central Terminal (G, H)", "South Terminal (J)"],
    annualPassengers: "52.3",
    hubAirlines: ["American Airlines"],
    peakHours: {
      morning: "6:00 AM - 9:00 AM",
      evening: "4:00 PM - 8:00 PM",
      busiestDays: ["Thursday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$17/day (Flamingo Garage)",
      garage: "$22/day"
    },
    transitOptions: ["Metrorail Orange Line", "Miami Beach Airport Express", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Concourse D security is typically fastest",
      "International departures in evening create long waits",
      "Latin America flight schedules create afternoon peaks"
    ],
    tipsGeneral: [
      "MIA Mover connects to Metrorail",
      "All concourses connected post-security",
      "Major gateway for Latin America and Caribbean"
    ],
    avgDomesticWait: "20-30 minutes",
    avgInternationalWait: "30-50 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  BOS: {
    code: "BOS",
    terminals: 4,
    terminalNames: ["Terminal A (Delta)", "Terminal B (United, Air Canada, Spirit)", "Terminal C (JetBlue)", "Terminal E (International)"],
    annualPassengers: "42.5",
    hubAirlines: ["JetBlue Airways", "Delta Air Lines"],
    peakHours: {
      morning: "5:30 AM - 8:30 AM",
      evening: "3:30 PM - 6:30 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$27/day (Economy lot with shuttle)",
      garage: "$38/day (Central Parking)",
      valet: "$45/day"
    },
    transitOptions: [
      "MBTA Blue Line (direct to downtown, $2.90)",
      "Silver Line SL1 (FREE from airport to South Station)",
      "Logan Express buses (to suburbs: Braintree, Framingham, Woburn, Peabody)",
      "Water taxi and ferry to downtown Boston",
      "Rental cars (on-site consolidated facility)",
      "Rideshare (Uber/Lyft pickup at each terminal)"
    ],
    tipsSecurity: [
      "Terminal C (JetBlue) handles heavy volume - expect 25-40 min waits in peak",
      "Terminal A (Delta) has most consistent and fastest processing",
      "Terminal B can be crowded with Spirit's budget travelers",
      "Terminal E (International) separate building - allow 20+ min walk time",
      "All terminals have PreCheck; CLEAR available at Terminals B, C, E"
    ],
    tipsGeneral: [
      "Silver Line SL1 is FREE from airport - connects to Red Line at South Station",
      "Blue Line runs every 5-8 min to downtown (Aquarium stop for waterfront)",
      "Terminals A and B are connected post-security - can walk between",
      "Terminal C is standalone - must exit and re-enter if connecting",
      "Legal Sea Foods in Terminal C for famous New England clam chowder"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  MSP: {
    code: "MSP",
    terminals: 2,
    terminalNames: ["Terminal 1 (Lindbergh)", "Terminal 2 (Humphrey)"],
    annualPassengers: "39.6",
    hubAirlines: ["Delta Air Lines", "Sun Country Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "3:30 PM - 6:30 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$14/day",
      garage: "$27/day",
      valet: "$36/day"
    },
    transitOptions: ["Metro Blue Line", "Metro Red Line", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal 1 has multiple checkpoints - T1 North often faster",
      "Terminal 2 serves budget carriers with separate security",
      "Delta hub creates consistent morning rushes"
    ],
    tipsGeneral: [
      "Light rail connects both terminals and downtown",
      "Terminal 1 is the main hub for Delta",
      "Known for efficiency despite cold weather"
    ],
    avgDomesticWait: "15-20 minutes",
    avgInternationalWait: "20-30 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  DTW: {
    code: "DTW",
    terminals: 2,
    terminalNames: ["McNamara Terminal (Delta)", "North Terminal"],
    annualPassengers: "36.4",
    hubAirlines: ["Delta Air Lines", "Spirit Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$13/day",
      garage: "$26/day"
    },
    transitOptions: ["SMART buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "McNamara Terminal has two security checkpoints",
      "North Terminal serves Spirit and other non-Delta carriers",
      "Express Tram in McNamara connects A and B/C concourses"
    ],
    tipsGeneral: [
      "McNamara Terminal has a mile-long underground walkway",
      "Tunnel light show connects A and B/C concourses",
      "Both terminals have dedicated security checkpoints"
    ],
    avgDomesticWait: "15-20 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  PHL: {
    code: "PHL",
    terminals: 7,
    terminalNames: ["Terminal A-East", "Terminal A-West", "Terminal B", "Terminal C", "Terminal D", "Terminal E", "Terminal F"],
    annualPassengers: "33.7",
    hubAirlines: ["American Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:30 AM",
      evening: "3:30 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$15/day",
      garage: "$29/day"
    },
    transitOptions: ["SEPTA Regional Rail", "SEPTA buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal A-West typically has shorter waits",
      "Terminal B and C handle most American traffic",
      "Terminals are connected airside post-security"
    ],
    tipsGeneral: [
      "Regional Rail direct to Center City Philadelphia",
      "Known for cheesesteaks at various restaurants",
      "All terminals connected post-security"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  LGA: {
    code: "LGA",
    terminals: 2,
    terminalNames: ["Terminal B (American, Southwest, United, JetBlue)", "Terminal C (Delta)"],
    annualPassengers: "31.3",
    hubAirlines: ["Delta Air Lines", "American Airlines"],
    peakHours: {
      morning: "6:00 AM - 9:00 AM",
      evening: "4:00 PM - 8:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$18/day (off-site economy lots)",
      garage: "$39/day (Terminal Garages P2, P4)",
      valet: "$55/day"
    },
    transitOptions: [
      "Q70 SBS (free bus to Jackson Heights subway - 7/E/F/M/R trains)",
      "M60 SBS to Harlem and connects to multiple subway lines",
      "LaGuardia Link Q70 to Woodside LIRR",
      "NYC Ferry from Manhattan (via shuttle)",
      "Rental cars (off-site with shuttle)",
      "Rideshare (designated zones at each terminal)"
    ],
    tipsSecurity: [
      "Terminal B security varies by concourse - B/C gates often faster than eastern gates",
      "Terminal C (Delta) has newest, most efficient checkpoint with automated lanes",
      "Morning business travel (6-9 AM) creates longest waits at both terminals",
      "PreCheck available at all checkpoints; CLEAR available at Terminal C",
      "Flight delays common due to NYC airspace congestion - check status often"
    ],
    tipsGeneral: [
      "$8 billion terminal rebuild completed 2024 - completely modernized",
      "No rail connection yet - Q70 SBS is fastest public transit (free, every 5-10 min)",
      "Primarily domestic and Canada flights only (per historical perimeter rule)",
      "Terminals B and C are NOT connected - verify correct terminal before entry",
      "Terminal C (Delta) has Shake Shack, Irving Farm Coffee; Terminal B has wide food court"
    ],
    avgDomesticWait: "20-30 minutes",
    avgInternationalWait: "N/A (primarily domestic)",
    preCheckLanes: true,
    clearLanes: true
  },
  SAT: {
    code: "SAT",
    terminals: 2,
    terminalNames: ["Terminal A", "Terminal B"],
    annualPassengers: "11.8",
    hubAirlines: ["Southwest Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:00 AM",
      evening: "3:30 PM - 6:00 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$8/day",
      garage: "$18/day"
    },
    transitOptions: ["VIA Metropolitan Transit", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal A handles most traffic",
      "Terminal B typically has shorter security lines",
      "Both terminals have TSA PreCheck lanes"
    ],
    tipsGeneral: [
      "Smaller, easier to navigate airport",
      "About 8 miles from downtown San Antonio",
      "Free WiFi throughout"
    ],
    avgDomesticWait: "10-20 minutes",
    avgInternationalWait: "15-25 minutes",
    preCheckLanes: true,
    clearLanes: false
  },
  SLC: {
    code: "SLC",
    terminals: 1,
    terminalNames: ["The New SLC (opened 2020, Concourses A and B)"],
    annualPassengers: "27.0",
    hubAirlines: ["Delta Air Lines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$12/day",
      garage: "$24/day"
    },
    transitOptions: ["TRAX Green Line", "UTA buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Brand new terminal has efficient security design",
      "Centralized security for all gates",
      "Delta hub creates morning congestion"
    ],
    tipsGeneral: [
      "Entirely new terminal opened 2020",
      "TRAX light rail to downtown Salt Lake City",
      "Mountain views from terminal windows"
    ],
    avgDomesticWait: "15-20 minutes",
    avgInternationalWait: "20-30 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  FLL: {
    code: "FLL",
    terminals: 4,
    terminalNames: ["Terminal 1 (Spirit, Frontier)", "Terminal 2 (Delta, United)", "Terminal 3 (Southwest, Alaska)", "Terminal 4 (JetBlue, American, International)"],
    annualPassengers: "35.9",
    hubAirlines: ["Spirit Airlines", "JetBlue Airways", "Southwest Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:30 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Saturday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$13/day (Hibiscus lot with shuttle)",
      garage: "$22/day (Terminal garages)",
      valet: "$25/day"
    },
    transitOptions: [
      "Tri-Rail (free shuttle to station, connects to Miami/West Palm Beach)",
      "Broward County Transit (Routes 1, 10, 40)",
      "Brightline (via transfer at Fort Lauderdale station)",
      "Rental cars (on-site Rental Car Center)",
      "Rideshare (pickup on lower level each terminal)"
    ],
    tipsSecurity: [
      "Terminal 1 (Spirit) has notoriously long waits - arrive 2.5+ hours early",
      "Terminal 4 checkpoint is newest and most efficient",
      "Terminal 3 (Southwest) steady but manageable lines",
      "Cruise ship embarkation days (Saturday/Sunday) create massive morning crowds",
      "PreCheck available at all terminals, CLEAR at Terminal 4 only"
    ],
    tipsGeneral: [
      "Just 4 miles from Fort Lauderdale Beach - popular pre-cruise destination",
      "Alternative to MIA with often cheaper flights and less congestion",
      "Port Everglades cruise terminal is 5-minute drive away",
      "Terminals are NOT connected airside - recheck which terminal you need",
      "Terminal 4 has best food court including Shula Burger and Casavana"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  BWI: {
    code: "BWI",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses A, B, C, D, E)"],
    annualPassengers: "26.5",
    hubAirlines: ["Southwest Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$12/day",
      garage: "$24/day"
    },
    transitOptions: ["MARC Train", "Amtrak", "Light Rail", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Checkpoint near Concourse D typically fastest",
      "Southwest dominance creates predictable patterns",
      "Central checkpoint can be congested"
    ],
    tipsGeneral: [
      "MARC and Amtrak connection via free shuttle",
      "Serves Baltimore, DC, and Northern Virginia",
      "Known for affordable parking options"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  DCA: {
    code: "DCA",
    terminals: 3,
    terminalNames: ["Terminal A", "Terminal B/C (National Hall)"],
    annualPassengers: "24.8",
    hubAirlines: ["American Airlines"],
    peakHours: {
      morning: "6:00 AM - 9:00 AM",
      evening: "4:00 PM - 8:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$22/day",
      garage: "$39/day"
    },
    transitOptions: ["Metro Yellow/Blue Lines", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal B/C security is typically busier",
      "Terminal A handles fewer airlines",
      "Congressional travel creates peak patterns"
    ],
    tipsGeneral: [
      "Metro direct to downtown DC",
      "Only domestic flights (per perimeter rule)",
      "Closest airport to DC monuments"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "N/A (domestic only)",
    preCheckLanes: true,
    clearLanes: true
  },
  IAD: {
    code: "IAD",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses A, B, C, D)"],
    annualPassengers: "24.5",
    hubAirlines: ["United Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:30 AM",
      evening: "3:30 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday"]
    },
    parking: {
      economy: "$17/day (Economy lot with shuttle)",
      garage: "$30/day (Hourly lots)",
      valet: "$35/day"
    },
    transitOptions: [
      "Metro Silver Line (direct to DC, opened 2022)",
      "Washington Flyer Coach to West Falls Church Metro",
      "Fairfax Connector buses",
      "Rental cars (on-site facility)",
      "Rideshare (designated pickup zones)"
    ],
    tipsSecurity: [
      "East Security is typically faster during morning rush",
      "West Security better for afternoon/evening international flights",
      "International departures to Europe (4-7 PM) create heavy congestion",
      "Mobile lounges add 10-15 min to reach Concourse C/D - plan accordingly",
      "CLEAR and PreCheck available at both checkpoints"
    ],
    tipsGeneral: [
      "Metro Silver Line provides 45-minute direct service to downtown DC",
      "Iconic Eero Saarinen-designed main terminal is a National Historic Landmark",
      "Mobile lounges (unique to IAD) transport passengers to midfield concourses",
      "AeroTrain connects main terminal to Concourses A/B quickly",
      "Concourse B has best dining options including Five Guys and Potbelly"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  MDW: {
    code: "MDW",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses A, B, C)"],
    annualPassengers: "22.3",
    hubAirlines: ["Southwest Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$17/day",
      garage: "$29/day"
    },
    transitOptions: ["CTA Orange Line", "Pace buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Single security checkpoint for all concourses",
      "Southwest dominates - consistent patterns",
      "Checkpoint can get congested during peak times"
    ],
    tipsGeneral: [
      "Orange Line direct to downtown Chicago",
      "Smaller alternative to O'Hare",
      "Known for quick connections"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "N/A (primarily domestic)",
    preCheckLanes: true,
    clearLanes: false
  },
  SAN: {
    code: "SAN",
    terminals: 2,
    terminalNames: ["Terminal 1", "Terminal 2"],
    annualPassengers: "25.6",
    hubAirlines: ["Southwest Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:30 AM",
      evening: "3:30 PM - 6:30 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$15/day",
      garage: "$34/day"
    },
    transitOptions: ["Bus Route 992", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Terminal 2 West has newer security setup",
      "Terminal 1 handles Southwest traffic",
      "Both terminals have TSA PreCheck"
    ],
    tipsGeneral: [
      "Very close to downtown San Diego",
      "No rail connection currently",
      "Terminal 1 renovation in progress"
    ],
    avgDomesticWait: "15-25 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  TPA: {
    code: "TPA",
    terminals: 1,
    terminalNames: ["Main Terminal (Airsides A, C, E, F)"],
    annualPassengers: "23.4",
    hubAirlines: ["Southwest Airlines"],
    peakHours: {
      morning: "5:30 AM - 8:00 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Saturday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$12/day",
      garage: "$19/day"
    },
    transitOptions: ["HART buses", "SkyConnect to rental cars", "Rideshare"],
    tipsSecurity: [
      "Security at each airside - can be faster",
      "Airside F often has shortest waits",
      "Theme park traffic creates weekend peaks"
    ],
    tipsGeneral: [
      "Unique design with shuttles to airsides",
      "Consistently rated among best US airports",
      "Free WiFi throughout"
    ],
    avgDomesticWait: "10-20 minutes",
    avgInternationalWait: "20-30 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  PDX: {
    code: "PDX",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses A, B, C, D, E)"],
    annualPassengers: "20.4",
    hubAirlines: ["Alaska Airlines", "Southwest Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Monday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$14/day",
      garage: "$33/day"
    },
    transitOptions: ["MAX Red Line", "TriMet buses", "Rental cars", "Rideshare"],
    tipsSecurity: [
      "Main security feeds all concourses",
      "Known for efficient TSA processing",
      "PreCheck lines typically very short"
    ],
    tipsGeneral: [
      "Consistently voted best US airport",
      "MAX Red Line direct to downtown Portland",
      "Famous carpet has cult following"
    ],
    avgDomesticWait: "10-20 minutes",
    avgInternationalWait: "15-25 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  RSW: {
    code: "RSW",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses B, C and D)"],
    annualPassengers: "10.3",
    hubAirlines: ["Southwest Airlines", "Delta Air Lines", "American Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Friday", "Saturday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$10/day",
      garage: "$16/day"
    },
    transitOptions: ["LeeTran bus route 60", "Rental cars (on-site centre)", "Rideshare (Uber/Lyft)", "Hotel shuttles"],
    tipsSecurity: [
      "Southwest Florida gets extremely busy in winter high season (January to April) — arrive earlier than you would in summer",
      "TSA PreCheck lanes are available at the main checkpoint",
      "Concourse B is closest to the checkpoint"
    ],
    tipsGeneral: [
      "One central checkpoint serves all three concourses",
      "Most dining is landside before security — eat before you screen",
      "Free WiFi throughout the terminal"
    ],
    avgDomesticWait: "10-25 minutes",
    avgInternationalWait: "15-30 minutes",
    preCheckLanes: true,
    clearLanes: false
  },
  AUS: {
    code: "AUS",
    terminals: 2,
    terminalNames: ["Barbara Jordan Terminal", "South Terminal"],
    annualPassengers: "22.0",
    hubAirlines: ["Southwest Airlines", "American Airlines", "Delta Air Lines"],
    peakHours: {
      morning: "4:30 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$12/day",
      garage: "$27/day"
    },
    transitOptions: ["CapMetro Route 20 and Airport Flyer", "Rental cars", "Rideshare (Uber/Lyft)", "Hotel shuttles"],
    tipsSecurity: [
      "Austin's checkpoints back up badly during SXSW, ACL and Formula 1 weekends",
      "The West checkpoint is usually shorter than the East checkpoint",
      "TSA PreCheck is available at both main checkpoints"
    ],
    tipsGeneral: [
      "The Barbara Jordan Terminal handles nearly all flights; the South Terminal is a separate building",
      "Live music stages operate in the main terminal most days",
      "Local Austin food is genuinely good here — allow time"
    ],
    avgDomesticWait: "15-30 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  BNA: {
    code: "BNA",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses A, B, C and D)"],
    annualPassengers: "21.9",
    hubAirlines: ["Southwest Airlines", "American Airlines", "Delta Air Lines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Thursday", "Friday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$16/day",
      garage: "$26/day",
      valet: "$40/day"
    },
    transitOptions: ["WeGo Route 18 bus", "Rental cars", "Rideshare (Uber/Lyft)", "Hotel shuttles"],
    tipsSecurity: [
      "Nashville has grown fast and morning lines can be long — arrive 2 hours early",
      "The checkpoint at the far end of the terminal is often quieter",
      "TSA PreCheck and CLEAR are both available"
    ],
    tipsGeneral: [
      "All concourses connect after security",
      "Live music stages are spread through the terminal",
      "The new international arrivals facility handles overseas flights"
    ],
    avgDomesticWait: "15-30 minutes",
    avgInternationalWait: "20-35 minutes",
    preCheckLanes: true,
    clearLanes: true
  },
  MSY: {
    code: "MSY",
    terminals: 1,
    terminalNames: ["Main Terminal (Concourses A, B and C)"],
    annualPassengers: "12.6",
    hubAirlines: ["Southwest Airlines", "Delta Air Lines", "American Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "3:00 PM - 6:00 PM",
      busiestDays: ["Friday", "Saturday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$16/day",
      garage: "$25/day"
    },
    transitOptions: ["Jefferson Transit E-2 bus", "Rental cars", "Rideshare (Uber/Lyft)", "Airport shuttle vans"],
    tipsSecurity: [
      "The new terminal has three checkpoints — the Concourse A checkpoint is usually quietest",
      "Mardi Gras and Jazz Fest weeks are exceptionally busy",
      "TSA PreCheck lanes are available at the main checkpoints"
    ],
    tipsGeneral: [
      "All concourses connect after security",
      "Local New Orleans food outlets are past security",
      "The terminal opened in 2019 and is well signposted"
    ],
    avgDomesticWait: "10-25 minutes",
    avgInternationalWait: "15-30 minutes",
    preCheckLanes: true,
    clearLanes: false
  },
  RDU: {
    code: "RDU",
    terminals: 2,
    terminalNames: ["Terminal 1", "Terminal 2"],
    annualPassengers: "14.6",
    hubAirlines: ["American Airlines", "Delta Air Lines", "Southwest Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$9/day",
      garage: "$26/day"
    },
    transitOptions: ["GoTriangle Route 100 bus", "Rental cars", "Rideshare (Uber/Lyft)", "Hotel shuttles"],
    tipsSecurity: [
      "Terminal 2 handles most traffic and has the longer lines",
      "Terminal 1 (Southwest, Frontier, Avelo) is usually quicker",
      "TSA PreCheck is available in both terminals"
    ],
    tipsGeneral: [
      "Terminals 1 and 2 are separate buildings — do not go to the wrong one",
      "A free shuttle links the two terminals landside",
      "Terminal 2 has the better dining selection"
    ],
    avgDomesticWait: "10-20 minutes",
    avgInternationalWait: "15-30 minutes",
    preCheckLanes: true,
    clearLanes: false
  },
  HNL: {
    code: "HNL",
    terminals: 3,
    terminalNames: ["Terminal 1 (Hawaiian)", "Terminal 2 (Main Overseas)", "Terminal 3 (Commuter)"],
    annualPassengers: "20.5",
    hubAirlines: ["Hawaiian Airlines", "Southwest Airlines", "United Airlines"],
    peakHours: {
      morning: "6:00 AM - 9:00 AM",
      evening: "8:00 PM - 11:00 PM",
      busiestDays: ["Friday", "Saturday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$18/day",
      garage: "$25/day"
    },
    transitOptions: ["TheBus routes 19 and 20", "Skyline rail (Airport Station)", "Rental cars (consolidated facility)", "Rideshare (Uber/Lyft)"],
    tipsSecurity: [
      "Mainland-bound evening departures cluster tightly — arrive 2.5 hours early",
      "Agricultural inspection is required before check-in for mainland flights, so add extra time",
      "TSA PreCheck is available at the main checkpoints"
    ],
    tipsGeneral: [
      "The Wiki Wiki shuttle links terminals landside",
      "Much of the airport is open-air — allow for weather",
      "Interisland flights use Terminal 1"
    ],
    avgDomesticWait: "15-30 minutes",
    avgInternationalWait: "25-40 minutes",
    preCheckLanes: true,
    clearLanes: false
  },
  OAK: {
    code: "OAK",
    terminals: 2,
    terminalNames: ["Terminal 1", "Terminal 2"],
    annualPassengers: "11.2",
    hubAirlines: ["Southwest Airlines", "Spirit Airlines", "Alaska Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Thursday", "Friday", "Sunday", "Monday"]
    },
    parking: {
      economy: "$19/day",
      garage: "$27/day"
    },
    transitOptions: ["BART Oakland Airport Connector", "AC Transit bus", "Rental cars", "Rideshare (Uber/Lyft)"],
    tipsSecurity: [
      "Oakland is usually much quicker through security than SFO",
      "Terminal 2 (Southwest) is the busier of the two",
      "TSA PreCheck is available in both terminals"
    ],
    tipsGeneral: [
      "The BART connector takes about 8 minutes from Coliseum Station",
      "Terminals 1 and 2 are a short walk apart landside",
      "A good alternative to SFO when fares are similar"
    ],
    avgDomesticWait: "10-20 minutes",
    avgInternationalWait: "15-25 minutes",
    preCheckLanes: true,
    clearLanes: false
  },
  SJC: {
    code: "SJC",
    terminals: 2,
    terminalNames: ["Terminal A", "Terminal B"],
    annualPassengers: "11.9",
    hubAirlines: ["Southwest Airlines", "Alaska Airlines", "American Airlines"],
    peakHours: {
      morning: "5:00 AM - 8:00 AM",
      evening: "4:00 PM - 7:00 PM",
      busiestDays: ["Monday", "Thursday", "Friday", "Sunday"]
    },
    parking: {
      economy: "$20/day",
      garage: "$30/day"
    },
    transitOptions: ["VTA Route 60 to Milpitas BART", "Caltrain via free Route 10 shuttle", "Rental cars", "Rideshare (Uber/Lyft)"],
    tipsSecurity: [
      "San Jose is consistently faster than SFO for Bay Area departures",
      "Terminal B (Southwest) handles the most traffic",
      "TSA PreCheck and CLEAR are available"
    ],
    tipsGeneral: [
      "Terminals A and B are connected landside by a walkway",
      "Close to downtown San Jose — about 10 minutes by car",
      "Good option for Silicon Valley business travel"
    ],
    avgDomesticWait: "10-20 minutes",
    avgInternationalWait: "15-25 minutes",
    preCheckLanes: true,
    clearLanes: true
  }
};

// Get airport details by code
export const getAirportDetails = (code: string): AirportDetails | undefined => {
  return airportDetails[code.toUpperCase()];
};
